import React, { useState } from 'react';
import { Send, CheckCircle, XCircle } from 'lucide-react';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focused, setFocused] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ type: 'success', message: 'Thank you! Your message has been sent.' });
        setFormData({ name: '', email: '', message: '' });
      } else {
        setStatus({ type: 'error', message: data.message || 'Something went wrong. Please try again.' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Failed to send message. Please try again later.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="relative">
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
          Name
        </label>
        <div className="relative">
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            onFocus={() => setFocused('name')}
            onBlur={() => setFocused('')}
            className={`w-full px-4 py-3 border-2 rounded-lg transition-all duration-300 ${
              focused === 'name'
                ? 'border-primary-500 ring-4 ring-primary-100 shadow-lg'
                : 'border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200'
            }`}
            placeholder="Your name"
          />
          {focused === 'name' && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
            </div>
          )}
        </div>
      </div>

      <div className="relative">
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
          Email
        </label>
        <div className="relative">
          <input
            type="email"
            id="email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            onFocus={() => setFocused('email')}
            onBlur={() => setFocused('')}
            className={`w-full px-4 py-3 border-2 rounded-lg transition-all duration-300 ${
              focused === 'email'
                ? 'border-primary-500 ring-4 ring-primary-100 shadow-lg'
                : 'border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200'
            }`}
            placeholder="your.email@example.com"
          />
          {focused === 'email' && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
            </div>
          )}
        </div>
      </div>

      <div className="relative">
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
          Message
        </label>
        <div className="relative">
          <textarea
            id="message"
            name="message"
            required
            rows="5"
            value={formData.message}
            onChange={handleChange}
            onFocus={() => setFocused('message')}
            onBlur={() => setFocused('')}
            className={`w-full px-4 py-3 border-2 rounded-lg transition-all duration-300 resize-none ${
              focused === 'message'
                ? 'border-primary-500 ring-4 ring-primary-100 shadow-lg'
                : 'border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200'
            }`}
            placeholder="Tell us about your project..."
          />
          {focused === 'message' && (
            <div className="absolute right-3 top-3">
              <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
            </div>
          )}
        </div>
      </div>

      {status.message && (
        <div
          className={`p-4 rounded-lg flex items-center gap-3 animate-slide-up ${
            status.type === 'success'
              ? 'bg-green-50 text-green-800 border-2 border-green-200'
              : 'bg-red-50 text-red-800 border-2 border-red-200'
          }`}
        >
          {status.type === 'success' ? (
            <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
          ) : (
            <XCircle className="text-red-600 flex-shrink-0" size={24} />
          )}
          <span className="font-medium">{status.message}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="group w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] tech-glow"
      >
        {isSubmitting ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>Sending...</span>
          </>
        ) : (
          <>
            <Send size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            <span>Send Message</span>
          </>
        )}
      </button>
    </form>
  );
};

export default ContactForm;
